import { Fragment, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildPatchPayload, omitNullish, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDateTime, fromEpochMsToInputValue, toEpochMs } from '../../lib/format';
import type { EventSession, LoctopusEvent, TicketType, TicketTypeKind } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  HelpPanel,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  Textarea,
  Toggle,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState('sessions');

  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.events.get(eventId!),
    enabled: !!eventId,
  });

  if (!eventId) return null;
  if (eventQuery.isLoading) return <Spinner />;
  if (eventQuery.error || !eventQuery.data) {
    return (
      <ErrorSummary error={toParsedError(eventQuery.error ?? new Error('Event not found.'))} />
    );
  }

  const event = eventQuery.data;

  return (
    <div>
      <PageHeader
        title={event.title}
        description={`${event.venueName ?? 'No venue set'} · ${event.timezone} · ${event.mode} mode`}
        actions={
          <>
            <Badge tone={event.status === 'cancelled' ? 'danger' : 'info'}>{event.status}</Badge>
            <RequirePermission permission="events.publish">
              <PublishButton eventId={event.id} status={event.status} />
            </RequirePermission>
            <Link to={`/events/${event.id}/seat-map`}>
              <Button variant="secondary">Seat map editor</Button>
            </Link>
            <Button variant="ghost" onClick={() => navigate('/events')}>
              Back to events
            </Button>
          </>
        }
      />
      <Tabs
        tabs={[
          { key: 'sessions', label: 'Sessions' },
          { key: 'ticket-types', label: 'Ticket types' },
          { key: 'about', label: 'About this event' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'sessions' && <SessionsPanel eventId={event.id} />}
      {tab === 'ticket-types' && <TicketTypesPanel eventId={event.id} />}
      {tab === 'about' && <AboutContentPanel event={event} />}
    </div>
  );
}

function PublishButton({ eventId, status }: { eventId: string; status: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      api.events.update(eventId, { status: status === 'published' ? 'draft' : 'published' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event', eventId] }),
  });
  return (
    <Button variant="secondary" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
      {status === 'published' ? 'Unpublish' : 'Publish'}
    </Button>
  );
}

function SessionsPanel({ eventId }: { eventId: string }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EventSession | null>(null);
  const sessionsQuery = useQuery({
    queryKey: ['sessions', eventId],
    queryFn: () => api.sessions.list(eventId, { limit: 100 }),
  });

  return (
    <Card
      title="Sessions"
      actions={
        <RequirePermission permission="sessions.manage">
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            Add session
          </Button>
        </RequirePermission>
      }
    >
      {sessionsQuery.isLoading ? (
        <Spinner />
      ) : (
        <DataTable<EventSession>
          columns={[
            { key: 'starts', header: 'Starts', render: (s) => formatDateTime(s.startsAt) },
            { key: 'ends', header: 'Ends', render: (s) => formatDateTime(s.endsAt) },
            { key: 'capacity', header: 'Capacity', render: (s) => s.capacityTotal },
            { key: 'status', header: 'Status', render: (s) => <Badge>{s.status}</Badge> },
          ]}
          rows={sessionsQuery.data?.data ?? []}
          getRowKey={(s) => s.id}
          onRowClick={(s) => {
            setEditing(s);
            setModalOpen(true);
          }}
          emptyMessage="No sessions scheduled yet."
        />
      )}
      {/* REAL BUG FOUND AND FIXED: this modal component is always mounted by its parent (only the
          inner <Modal> itself toggles null/non-null on `open`), so its `useState(session?.x ?? ...)`
          initializers only ever ran ONCE, at this component's first-ever mount - every subsequent
          click on "Add session" or any existing row re-rendered the SAME instance with a new
          `session` prop that its state never picked up. In practice this meant "Edit session"
          always opened showing blank/default fields instead of the session's real values,
          regardless of which row was clicked - not a clear-vs-untouched issue, a plain stale-
          initial-state bug, pre-existing and independent of this round's fix but caught while
          live-verifying it (see TicketTypeFormModal below, which had the identical bug and is this
          round's actual focus). `key` forces a real remount (and so a fresh useState run) every
          time the target record changes - 'new' for the create form, the row's own id for edit. */}
      <SessionFormModal
        key={editing?.id ?? 'new'}
        eventId={eventId}
        session={editing}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Card>
  );
}

function SessionFormModal({
  eventId,
  session,
  open,
  onClose,
}: {
  eventId: string;
  session: EventSession | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [startsAt, setStartsAt] = useState(fromEpochMsToInputValue(session?.startsAt));
  const [endsAt, setEndsAt] = useState(fromEpochMsToInputValue(session?.endsAt));
  const [capacity, setCapacity] = useState(String(session?.capacityTotal ?? 100));
  const [status, setStatus] = useState(session?.status ?? 'scheduled');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        startsAt: toEpochMs(startsAt),
        endsAt: toEpochMs(endsAt),
        capacityTotal: Number(capacity),
        status,
      };
      return session ? api.sessions.update(session.id, body) : api.sessions.create(eventId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', eventId] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={session ? 'Edit session' : 'Add session'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} />}
      <FormField label="Starts at">
        <Input
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
        />
      </FormField>
      <FormField label="Ends at">
        <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
      </FormField>
      <FormField label="Capacity">
        <Input
          type="number"
          min={0}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
        />
      </FormField>
      {session && (
        <FormField label="Status">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as EventSession['status'])}
          >
            <option value="scheduled">Scheduled</option>
            <option value="on_sale">On sale</option>
            <option value="sold_out">Sold out</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </FormField>
      )}
    </Modal>
  );
}

function TicketTypesPanel({ eventId }: { eventId: string }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TicketType | null>(null);
  const ticketTypesQuery = useQuery({
    queryKey: ['ticket-types', eventId],
    queryFn: () => api.ticketTypes.list(eventId, { limit: 100 }),
  });

  return (
    <Card
      title="Ticket types"
      actions={
        <RequirePermission permission="ticket_types.manage">
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            Add ticket type
          </Button>
        </RequirePermission>
      }
    >
      {ticketTypesQuery.isLoading ? (
        <Spinner />
      ) : (
        <DataTable<TicketType>
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (t) => (
                <span className="font-medium text-slate-800">
                  {t.name} {t.isHidden && <Badge tone="warning">hidden</Badge>}
                </span>
              ),
            },
            { key: 'kind', header: 'Kind', render: (t) => t.kind },
            {
              key: 'price',
              header: 'Price',
              render: (t) => `${(t.priceMinor / 100).toFixed(2)} ${t.currency}`,
            },
            { key: 'capacity', header: 'Capacity', render: (t) => t.capacity ?? 'Unlimited' },
            { key: 'perOrder', header: 'Per-order limit', render: (t) => t.perOrderLimit ?? '-' },
            {
              key: 'sale',
              header: 'Sale window',
              render: (t) =>
                t.saleStartsAt || t.saleEndsAt
                  ? `${formatDateTime(t.saleStartsAt)} - ${formatDateTime(t.saleEndsAt)}`
                  : 'Always on sale',
            },
          ]}
          rows={ticketTypesQuery.data?.data ?? []}
          getRowKey={(t) => t.id}
          onRowClick={(t) => {
            setEditing(t);
            setModalOpen(true);
          }}
          emptyMessage="No ticket types yet."
        />
      )}
      {/* REAL, SEVERE BUG FOUND AND FIXED while live-verifying the clear-vs-untouched fix below:
          this modal is always mounted (only the inner <Modal> toggles on `open`), so every
          `useState(ticketType?.x ?? ...)` initializer below only ever ran ONCE, at this
          component's first mount - "Edit ticket type" always opened with every field blank/
          default, never the real values of whichever row was clicked, confirmed live (a fresh
          page load, click a ticket type with capacity=50/perOrderLimit=3/name set, and the modal
          opened with Name/Price/Capacity/Per-order limit all empty). This isn't the clear-vs-
          untouched bug this round is about - it's worse, a plain stale-initial-state bug that
          predates it - but it directly undermines that fix if left in place: a user "leaving a
          field untouched" can't tell blank-because-never-set apart from blank-because-this-form-
          never-loaded-it, and might unintentionally submit a real clear. `key` forces React to
          actually remount (re-run every useState initializer) whenever the edited record changes
          - 'new' for the create form, the row's own id for edit - closing that gap. */}
      <TicketTypeFormModal
        key={editing?.id ?? 'new'}
        eventId={eventId}
        ticketType={editing}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Card>
  );
}

function TicketTypeFormModal({
  eventId,
  ticketType,
  open,
  onClose,
}: {
  eventId: string;
  ticketType: TicketType | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(ticketType?.name ?? '');
  const [kind, setKind] = useState<TicketTypeKind>(ticketType?.kind ?? 'GA');
  const [priceMajor, setPriceMajor] = useState(String((ticketType?.priceMinor ?? 0) / 100));
  const [currency, setCurrency] = useState(ticketType?.currency ?? 'AUD');
  const [capacity, setCapacity] = useState(
    ticketType?.capacity != null ? String(ticketType.capacity) : '',
  );
  const [perOrderLimit, setPerOrderLimit] = useState(
    ticketType?.perOrderLimit != null ? String(ticketType.perOrderLimit) : '',
  );
  const [saleStartsAt, setSaleStartsAt] = useState(
    fromEpochMsToInputValue(ticketType?.saleStartsAt),
  );
  const [saleEndsAt, setSaleEndsAt] = useState(fromEpochMsToInputValue(ticketType?.saleEndsAt));
  const [isHidden, setIsHidden] = useState(ticketType?.isHidden ?? false);
  const [accessCode, setAccessCode] = useState(ticketType?.accessCode ?? '');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    // REAL BUG FOUND AND FIXED (CREATE path): capacity/perOrderLimit/saleStartsAt/saleEndsAt/
    // accessCode used to be sent as an explicit `null` when left blank -
    // createTicketTypeSchema's `.optional()` (apps/client-api/src/routes/management/schemas.ts)
    // only ever accepts a MISSING key or `undefined`, never an explicit null, so every GA ticket
    // type with unlimited capacity (the single most common real-world case) or no per-order
    // limit/sale window 400'd. `omitNullish` strips them instead - there's no prior value on a
    // CREATE, so "blank" can only ever mean "omit".
    //
    // FIXED (EDIT path, this was the documented known limitation - `docs/FORM_SWEEP_RESULTS.md`
    // "Known real gaps" #4): omitting a blank field on edit meant "don't change it", not
    // "clear it" - there was no way to un-set a previously-set capacity/sale-window/access-code
    // back to blank. `buildPatchPayload` (see `packages/shared/src/buildPatchPayload.ts` for the
    // canonical convention) diffs the form's current values against the ticket type as it was
    // loaded: unchanged fields are omitted, a field changed TO blank is sent as an explicit
    // `null` (now accepted - `updateTicketTypeSchema` is `.nullable()` for these fields), and a
    // field changed to a real value is sent with that value.
    mutationFn: () => {
      const current = {
        name,
        kind,
        priceMinor: Math.round(Number(priceMajor) * 100),
        currency,
        capacity: capacity ? Number(capacity) : null,
        perOrderLimit: perOrderLimit ? Number(perOrderLimit) : null,
        saleStartsAt: saleStartsAt ? toEpochMs(saleStartsAt) : null,
        saleEndsAt: saleEndsAt ? toEpochMs(saleEndsAt) : null,
        isHidden,
        accessCode: isHidden && accessCode ? accessCode : null,
      };
      if (!ticketType) {
        return api.ticketTypes.create(eventId, omitNullish(current));
      }
      const original = {
        name: ticketType.name,
        kind: ticketType.kind,
        priceMinor: ticketType.priceMinor,
        currency: ticketType.currency,
        capacity: ticketType.capacity ?? null,
        perOrderLimit: ticketType.perOrderLimit ?? null,
        saleStartsAt: ticketType.saleStartsAt ?? null,
        saleEndsAt: ticketType.saleEndsAt ?? null,
        isHidden: ticketType.isHidden ?? false,
        accessCode: ticketType.accessCode ?? null,
      };
      return api.ticketTypes.update(ticketType.id, buildPatchPayload(original, current));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-types', eventId] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={ticketType ? 'Edit ticket type' : 'Add ticket type'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} />}
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="General Admission"
          />
        </FormField>
        <FormField
          label="Kind"
          hint="GA = general admission, reserved = seat map, addon_bundle = bundled with an add-on"
        >
          <Select value={kind} onChange={(e) => setKind(e.target.value as TicketTypeKind)}>
            <option value="GA">GA</option>
            <option value="reserved">Reserved (seat map)</option>
            <option value="addon_bundle">Add-on bundle</option>
          </Select>
        </FormField>
        <FormField label="Price">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={priceMajor}
            onChange={(e) => setPriceMajor(e.target.value)}
          />
        </FormField>
        <FormField label="Currency">
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as TicketType['currency'])}
          >
            {['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Capacity" hint="Leave blank for unlimited">
          <Input
            type="number"
            min={0}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
          />
        </FormField>
        <FormField label="Per-order limit" hint="Max quantity per order">
          <Input
            type="number"
            min={1}
            value={perOrderLimit}
            onChange={(e) => setPerOrderLimit(e.target.value)}
          />
        </FormField>
        <FormField label="Sale starts" hint="Leave blank to go on sale immediately">
          <Input
            type="datetime-local"
            value={saleStartsAt}
            onChange={(e) => setSaleStartsAt(e.target.value)}
          />
        </FormField>
        <FormField label="Sale ends" hint="Leave blank for no end">
          <Input
            type="datetime-local"
            value={saleEndsAt}
            onChange={(e) => setSaleEndsAt(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Hidden / access-code only">
        <Toggle
          checked={isHidden}
          onChange={setIsHidden}
          label={isHidden ? 'Hidden from public listing' : 'Visible to all buyers'}
        />
      </FormField>
      {isHidden && (
        <FormField label="Access code" hint="Buyers must enter this code to see the ticket type">
          <Input
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            placeholder="VIP2026"
          />
        </FormField>
      )}
    </Modal>
  );
}

/**
 * Dashboard brief task 4 ("About this event" editor). Plain text with lightweight structure, no
 * markdown library, no HTML, no WYSIWYG: "#"/"##"/"###" headings, "-"/"*" bullet lines, blank-
 * line-separated paragraphs (consecutive plain lines inside one paragraph are joined with a line
 * break, not run together). RECONCILED mid-task, not just assumed - this is a byte-for-byte copy
 * of `apps/checkout/src/components/AboutContent.tsx`'s own `parseLightweightMarkdown` (found
 * uncommitted in this same working tree, built by the backend/checkout agent in parallel for the
 * buyer-facing read side of this exact field), duplicated here rather than imported (apps/checkout
 * is a different app, outside this task's file list) so this editor's live preview matches what
 * buyers will actually see, not a simplified approximation of it. Backed by the now-confirmed-real
 * `aboutContent` field on `LoctopusEvent` (`packages/db/src/schema/catalog.ts`'s `events.about
 * Content` column, added by that same agent) - saved through the existing, real
 * `api.events.update()` call, same as every other event field.
 */
function renderAboutContentPreview(text: string): ReactNode[] {
  const lines = text.split(/\r?\n/);
  const nodes: ReactNode[] = [];
  let listBuffer: string[] = [];
  let paraBuffer: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const items = listBuffer;
    listBuffer = [];
    nodes.push(
      <ul key={`ul-${key++}`} className="list-disc pl-5 space-y-1 text-sm text-slate-700">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>,
    );
  };

  const flushPara = () => {
    if (paraBuffer.length === 0) return;
    const linesInPara = paraBuffer;
    paraBuffer = [];
    nodes.push(
      <p key={`p-${key++}`} className="text-sm text-slate-700">
        {linesInPara.map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </p>,
    );
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = /^(#{1,3})\s+(.+)$/.exec(line);
    const bulletMatch = /^[-*]\s+(.+)$/.exec(line);

    if (headingMatch) {
      flushList();
      flushPara();
      const level = headingMatch[1]!.length;
      const headingText = headingMatch[2]!;
      const headingClass = 'font-heading font-semibold text-slate-800 mt-3 first:mt-0';
      if (level === 1) {
        nodes.push(
          <h4 key={`h-${key++}`} className={`${headingClass} text-base`}>
            {headingText}
          </h4>,
        );
      } else if (level === 2) {
        nodes.push(
          <h5 key={`h-${key++}`} className={`${headingClass} text-sm`}>
            {headingText}
          </h5>,
        );
      } else {
        nodes.push(
          <h6 key={`h-${key++}`} className={`${headingClass} text-sm`}>
            {headingText}
          </h6>,
        );
      }
    } else if (bulletMatch) {
      flushPara();
      listBuffer.push(bulletMatch[1]!);
    } else if (line === '') {
      flushList();
      flushPara();
    } else {
      flushList();
      paraBuffer.push(line);
    }
  }
  flushList();
  flushPara();
  return nodes;
}

function AboutContentPanel({ event }: { event: LoctopusEvent }) {
  const queryClient = useQueryClient();
  const [aboutContent, setAboutContent] = useState(event.aboutContent ?? '');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.events.update(event.id, { aboutContent: aboutContent.trim() ? aboutContent : null }),
    onSuccess: () => {
      setFormError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
    },
    onError: (err) => {
      setSaved(false);
      setFormError(toParsedError(err));
    },
  });

  const preview = renderAboutContentPreview(aboutContent);

  return (
    <RequirePermission permission="events.manage" fallback={<AboutContentReadOnly event={event} />}>
      <Card title="About this event">
        <HelpPanel storageKey="help-event-about" title="What is this?">
          This is the longer description buyers see on the event page - not just the short title,
          but the real details: what to expect, what's included, parking, age restrictions, and
          so on. Start a line with "#" for a heading and "-" for a bullet point; everything else
          is shown as a normal paragraph.
        </HelpPanel>
        {formError && <ErrorSummary error={formError} className="mb-3" />}
        {saved && !formError && (
          <p className="mb-3 text-sm text-emerald-700">About content saved.</p>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="About this event"
            hint='Start a line with "#" for a heading, "-" for a bullet point, a blank line for a new paragraph.'
          >
            <Textarea
              rows={12}
              value={aboutContent}
              onChange={(e) => {
                setAboutContent(e.target.value);
                setSaved(false);
              }}
              placeholder={'# What to expect\nDoors open at 6pm.\n\n- Free parking on site\n- All ages welcome'}
            />
          </FormField>
          <div>
            <p className="block text-sm font-medium text-slate-700 mb-1">Preview</p>
            <div className="border border-slate-200 rounded-brand p-3 min-h-[12rem] space-y-2">
              {preview.length === 0 ? (
                <p className="text-sm text-slate-400">Nothing written yet.</p>
              ) : (
                preview
              )}
            </div>
          </div>
        </div>
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="mt-4">
          {mutation.isPending ? 'Saving...' : 'Save about content'}
        </Button>
      </Card>
    </RequirePermission>
  );
}

function AboutContentReadOnly({ event }: { event: LoctopusEvent }) {
  const preview = renderAboutContentPreview(event.aboutContent ?? '');
  return (
    <Card title="About this event">
      <p className="text-sm text-slate-500 mb-3">
        You don't have permission to edit this event's "about" content.
      </p>
      {preview.length === 0 ? (
        <p className="text-sm text-slate-400">Nothing written yet.</p>
      ) : (
        <div className="space-y-2">{preview}</div>
      )}
    </Card>
  );
}
